/**
 * Shared by both payment screens: the dashboard's subscription invoice page and
 * the public tuition page. It lives here because `apps/web` cannot import
 * `@repo/ui` — this package is the only code both frontends share.
 */

/** How often a payment page re-checks while it is waiting. */
export const POLL_INTERVAL_MS = 5_000;

/**
 * How long it keeps checking. A page left open in a background tab would
 * otherwise poll forever, and the public tuition page is reachable by anyone
 * holding the link: ~120 requests per session is nothing, ~17k/day per
 * abandoned tab is not. Past the window the UI offers a manual re-check.
 */
export const POLL_WINDOW_MS = 10 * 60_000;

/**
 * Is money still owed on this invoice? PARTIAL counts — a part-paid tuition
 * invoice still has a balance, so its page is still worth watching.
 */
export function isAwaitingPayment(status: string): boolean {
  return status === 'PENDING' || status === 'PARTIAL';
}

/**
 * The whole polling rule in one place, so neither app can drift from the other.
 * Time is passed in rather than read, which is what makes this testable.
 */
export function shouldKeepPolling(args: {
  status: string;
  /** Epoch ms when the page mounted. */
  startedAt: number;
  now: number;
}): boolean {
  if (!isAwaitingPayment(args.status)) return false;
  return args.now - args.startedAt <= POLL_WINDOW_MS;
}
